"use client";

// Minimal top route-progress bar.
//
// Next's App Router server navigations have no built-in loading indicator, so
// clicking a link feels stale until the new page finishes rendering. This is a
// thin (2px) coral→sunset sliver that starts the instant an internal link is
// clicked (or back/forward fires) and completes when the new pathname lands.
// No dependency, no spinner — just a travel-warm gradient. Mounted once in the
// root layout so it covers marketing, /app, and /mobile alike.

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

export default function TopProgressBar() {
  const pathname = usePathname();
  const [progress, setProgress] = useState(0); // 0 = idle/hidden
  const trickle = useRef<ReturnType<typeof setInterval> | null>(null);
  const safety = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hide = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLoop = useCallback(() => {
    if (trickle.current) { clearInterval(trickle.current); trickle.current = null; }
    if (safety.current) { clearTimeout(safety.current); safety.current = null; }
  }, []);

  const finish = useCallback(() => {
    clearLoop();
    setProgress(100);
    if (hide.current) clearTimeout(hide.current);
    hide.current = setTimeout(() => setProgress(0), 320);
  }, [clearLoop]);

  const start = useCallback(() => {
    if (hide.current) { clearTimeout(hide.current); hide.current = null; }
    clearLoop();
    setProgress(10);
    // Ease toward 90% so the bar always shows motion while the server renders.
    trickle.current = setInterval(() => {
      setProgress((p) => (p >= 90 ? p : p + Math.max(0.5, (90 - p) * 0.1)));
    }, 180);
    // Safety net — never let the bar hang if the completion signal is missed.
    safety.current = setTimeout(finish, 12000);
  }, [clearLoop, finish]);

  // Navigation landed → complete (skip the initial mount).
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    finish();
  }, [pathname, finish]);

  // Start on internal link clicks + browser back/forward.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return; // external link
      if (url.pathname === window.location.pathname) return; // same page (hash/query only)
      start();
    };
    const onPop = () => start();
    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPop);
      clearLoop();
      if (hide.current) clearTimeout(hide.current);
    };
  }, [start, clearLoop]);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[100]"
      style={{
        height: 2,
        opacity: progress > 0 && progress < 100 ? 1 : 0,
        transition: "opacity 0.3s ease",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${progress}%`,
          background: "linear-gradient(90deg, #f26960 0%, #f7a072 55%, #ffd27a 100%)",
          boxShadow: "0 0 10px rgba(242,105,96,0.55), 0 0 4px rgba(242,105,96,0.4)",
          borderRadius: "0 9999px 9999px 0",
          transition: "width 0.18s ease",
        }}
      />
    </div>
  );
}
